import React from 'react';
import { humanizeKey } from '../../utils/humanizeKey';
import { hasMultipleOptions, getComponentOptions, getOptionTotal } from '../Quotes/optionUtils';
import {
  Sparkles,
  Castle,
  Hotel,
  Ticket,
  Wand2,
  Utensils,
  Plane,
  Car,
  Ship,
  MapPin,
  Calendar,
  Users,
  Clock,
  DollarSign,
  CheckCircle2
} from 'lucide-react';
import { TEMPLATE_PALETTES } from './templatePalettes';

// Voyager Pro / Figma reskin - 'magical' is a dark theme in Figma
// (near-black purple background, gold accent) rather than the previous
// light pastel purple/pink look. Rebuilt to that dark orientation per
// explicit user direction (full fidelity, not just an accent-color nudge).
const PALETTE = TEMPLATE_PALETTES.magical;

const MagicalTemplate = ({ quote, onPayment, isAgentPreview = false }) => {
  if (!quote) {
    return null;
  }

  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return '$0.00';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const [year, month, day] = dateString.split('-');
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatTime = (timeString) => {
    if (!timeString) return '';
    try {
      const date = new Date(`2000-01-01T${timeString}`);
      return date.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    } catch {
      return timeString;
    }
  };

  const getClientLastName = () => {
    if (quote.client?.lastName) return quote.client.lastName;
    if (quote.client?.firstName) return quote.client.firstName;
    return 'Your';
  };

  const getComponentIcon = (component) => {
    const type = component.type?.toLowerCase() || '';
    const title = component.title?.toLowerCase() || '';

    if (type.includes('hotel') || title.includes('hotel') || title.includes('resort')) {
      return <Castle className="w-8 h-8" style={{ color: 'var(--accent)' }} />;
    }
    if (type.includes('disney') || type.includes('universal') || type.includes('theme') ||
        type.includes('activity') || title.includes('park') || title.includes('ticket')) {
      return <Ticket className="w-8 h-8" style={{ color: 'var(--accent)' }} />;
    }
    if (type.includes('restaurant') || type.includes('dining') || title.includes('dining') || title.includes('restaurant')) {
      return <Utensils className="w-8 h-8" style={{ color: 'var(--accent)' }} />;
    }
    if (type.includes('flight') || title.includes('flight')) {
      return <Plane className="w-8 h-8" style={{ color: 'var(--accent)' }} />;
    }
    if (type.includes('car') || title.includes('car') || title.includes('rental')) {
      return <Car className="w-8 h-8" style={{ color: 'var(--accent)' }} />;
    }
    if (type.includes('cruise') || title.includes('cruise')) {
      return <Ship className="w-8 h-8" style={{ color: 'var(--accent)' }} />;
    }
    return <Wand2 className="w-8 h-8" style={{ color: 'var(--accent)' }} />;
  };

  const isThemeParkComponent = (component) => {
    const type = component.type?.toLowerCase() || '';
    const title = component.title?.toLowerCase() || '';
    return type.includes('disney') || type.includes('universal') || type.includes('theme') ||
           type.includes('activity') || title.includes('park') || title.includes('ticket');
  };

  return (
    <div className="min-h-screen overflow-x-hidden" style={{ ...PALETTE, backgroundColor: 'var(--background)' }}>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 sm:py-12">

        {/* Hero Section */}
        <div className="backdrop-blur-sm rounded-3xl shadow-xl p-6 sm:p-10 mb-8 sm:mb-12 text-center" style={{ backgroundColor: 'var(--muted)', border: '1px solid var(--border)' }}>
          <div className="flex items-center justify-center gap-3 mb-4">
            <Sparkles className="w-10 h-10 animate-pulse" style={{ color: 'var(--accent)' }} />
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold" style={{ color: 'var(--foreground)' }}>
              The {getClientLastName()} Family's Magical Adventure!
            </h1>
            <Sparkles className="w-10 h-10 animate-pulse" style={{ color: 'var(--accent)' }} />
          </div>

          {quote.quoteTitle && (
            <p className="text-lg sm:text-xl font-semibold mb-6" style={{ color: 'var(--accent)' }}>
              {quote.quoteTitle}
            </p>
          )}

          {/* Trip Overview */}
          <div className="flex flex-wrap justify-center gap-4 sm:gap-6 mt-6">
            {quote.travelDates?.start && (
              <div className="flex items-center justify-center gap-3 p-4 rounded-2xl" style={{ backgroundColor: 'var(--background)', border: '1px solid var(--border)' }}>
                <Calendar className="w-6 h-6" style={{ color: 'var(--accent)' }} />
                <div className="text-left">
                  <div className="text-xs font-semibold" style={{ color: 'var(--muted-foreground)' }}>Travel Dates</div>
                  <div className="text-sm font-bold" style={{ color: 'var(--foreground)' }}>{formatDate(quote.travelDates.start)}</div>
                  {quote.travelDates.end && (
                    <div className="text-xs" style={{ color: 'var(--muted-foreground)' }}>to {formatDate(quote.travelDates.end)}</div>
                  )}
                </div>
              </div>
            )}

            {(quote.travelers?.adults || quote.travelers?.children?.length) && (
              <div className="flex items-center justify-center gap-3 p-4 rounded-2xl" style={{ backgroundColor: 'var(--background)', border: '1px solid var(--border)' }}>
                <Users className="w-6 h-6" style={{ color: 'var(--accent)' }} />
                <div className="text-left">
                  <div className="text-xs font-semibold" style={{ color: 'var(--muted-foreground)' }}>Travelers</div>
                  <div className="text-sm font-bold" style={{ color: 'var(--foreground)' }}>
                    {quote.travelers.adults || 0} Adult{quote.travelers.adults !== 1 ? 's' : ''}
                    {quote.travelers.children?.length > 0 && `, ${quote.travelers.children.length} Child${quote.travelers.children.length !== 1 ? 'ren' : ''}`}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Itinerary Components */}
        <div className="space-y-6">
          <h2 className="text-2xl sm:text-3xl font-extrabold text-center mb-6" style={{ color: 'var(--foreground)' }}>
            ✨ Your Magical Itinerary ✨
          </h2>

          {quote.components && quote.components.length > 0 ? (
            <div className="space-y-4">
              {quote.components.map((component, index) => {
                const isThemePark = isThemeParkComponent(component);

                return (
                  <div
                    key={component.id || index}
                    className="backdrop-blur-sm rounded-3xl shadow-xl overflow-hidden"
                    style={{
                      backgroundColor: 'var(--muted)',
                      border: isThemePark ? '2px solid var(--accent)' : '1px solid var(--border)',
                    }}
                  >
                    {/* Component Header */}
                    <div className="p-4 sm:p-6" style={{ backgroundColor: 'var(--background)' }}>
                      {/* Mobile: Stacked Layout */}
                      <div className="block sm:hidden">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-3">
                            <div className="flex-shrink-0 p-2 rounded-2xl shadow-md" style={{ backgroundColor: 'var(--muted)' }}>
                              {getComponentIcon(component)}
                            </div>
                            <div className="text-2xl font-extrabold" style={{ color: 'var(--accent)' }}>
                              {formatCurrency(component.price)}
                            </div>
                          </div>
                        </div>
                        <h3 className="text-lg font-extrabold mb-2" style={{ color: 'var(--foreground)' }}>
                          {component.title || `Experience ${index + 1}`}
                        </h3>
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="inline-block px-2.5 py-1 rounded-full text-xs font-semibold" style={{ backgroundColor: 'var(--primary)', color: 'var(--foreground)' }}>
                            {component.type || 'Experience'}
                          </span>
                          {isThemePark && (
                            <span className="inline-block px-2.5 py-1 rounded-full text-xs font-semibold animate-pulse" style={{ backgroundColor: 'var(--accent)', color: 'var(--background)' }}>
                              ⭐ Main Event
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Desktop: Horizontal Layout */}
                      <div className="hidden sm:flex items-start gap-4">
                        <div className="flex-shrink-0 p-3 rounded-2xl shadow-md" style={{ backgroundColor: 'var(--muted)' }}>
                          {getComponentIcon(component)}
                        </div>

                        <div className="flex-1 min-w-0">
                          <h3 className="text-xl sm:text-2xl font-extrabold mb-2 break-words" style={{ color: 'var(--foreground)' }}>
                            {component.title || `Experience ${index + 1}`}
                          </h3>
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="inline-block px-3 py-1 rounded-full text-sm font-semibold" style={{ backgroundColor: 'var(--primary)', color: 'var(--foreground)' }}>
                              {component.type || 'Experience'}
                            </span>
                            {isThemePark && (
                              <span className="inline-block px-3 py-1 rounded-full text-sm font-semibold animate-pulse" style={{ backgroundColor: 'var(--accent)', color: 'var(--background)' }}>
                                ⭐ Main Event
                              </span>
                            )}
                          </div>
                        </div>

                        <div className="text-right flex-shrink-0">
                          <div className="text-2xl sm:text-3xl font-extrabold" style={{ color: 'var(--accent)' }}>
                            {formatCurrency(component.price)}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Component Details */}
                    {component.data && Object.keys(component.data).length > 0 && (
                      <div className="p-4 sm:p-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

                          {/* Hotel/Resort specific fields */}
                          {(component.type?.toLowerCase().includes('hotel') || component.title?.toLowerCase().includes('hotel') || component.title?.toLowerCase().includes('resort')) && (
                            <>
                              {component.data.property_name && (
                                <div className="flex items-start gap-3 p-4 rounded-2xl" style={{ backgroundColor: 'var(--background)' }}>
                                  <Hotel className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: 'var(--accent)' }} />
                                  <div>
                                    <div className="text-sm font-semibold" style={{ color: 'var(--foreground)' }}>Property</div>
                                    <div style={{ color: 'var(--muted-foreground)' }}>{component.data.property_name}</div>
                                  </div>
                                </div>
                              )}

                              {component.data.check_in_date && (
                                <div className="flex items-start gap-3 p-4 rounded-2xl" style={{ backgroundColor: 'var(--background)' }}>
                                  <Calendar className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: 'var(--accent)' }} />
                                  <div>
                                    <div className="text-sm font-semibold" style={{ color: 'var(--foreground)' }}>Check-In</div>
                                    <div className="font-bold" style={{ color: 'var(--muted-foreground)' }}>{formatDate(component.data.check_in_date)}</div>
                                  </div>
                                </div>
                              )}

                              {component.data.check_out_date && (
                                <div className="flex items-start gap-3 p-4 rounded-2xl" style={{ backgroundColor: 'var(--background)' }}>
                                  <Calendar className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: 'var(--accent)' }} />
                                  <div>
                                    <div className="text-sm font-semibold" style={{ color: 'var(--foreground)' }}>Check-Out</div>
                                    <div className="font-bold" style={{ color: 'var(--muted-foreground)' }}>{formatDate(component.data.check_out_date)}</div>
                                  </div>
                                </div>
                              )}

                              {component.data.room_type && (
                                <div className="flex items-start gap-3 p-4 rounded-2xl" style={{ backgroundColor: 'var(--background)' }}>
                                  <Castle className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: 'var(--accent)' }} />
                                  <div>
                                    <div className="text-sm font-semibold" style={{ color: 'var(--foreground)' }}>Room Type</div>
                                    <div style={{ color: 'var(--muted-foreground)' }}>{component.data.room_type}</div>
                                  </div>
                                </div>
                              )}
                            </>
                          )}

                          {/* Restaurant/Dining specific fields */}
                          {(component.type?.toLowerCase().includes('restaurant') || component.type?.toLowerCase().includes('dining') || component.title?.toLowerCase().includes('dining') || component.title?.toLowerCase().includes('restaurant')) && (
                            <>
                              {component.data.reservation_time && (
                                <div className="flex items-start gap-3 p-4 rounded-2xl" style={{ backgroundColor: 'var(--background)', border: '1px solid var(--accent)' }}>
                                  <Clock className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: 'var(--accent)' }} />
                                  <div>
                                    <div className="text-sm font-semibold" style={{ color: 'var(--foreground)' }}>Reservation Time</div>
                                    <div className="font-bold text-lg" style={{ color: 'var(--accent)' }}>{formatTime(component.data.reservation_time)}</div>
                                  </div>
                                </div>
                              )}

                              {component.data.reservation_date && (
                                <div className="flex items-start gap-3 p-4 rounded-2xl" style={{ backgroundColor: 'var(--background)' }}>
                                  <Calendar className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: 'var(--accent)' }} />
                                  <div>
                                    <div className="text-sm font-semibold" style={{ color: 'var(--foreground)' }}>Date</div>
                                    <div style={{ color: 'var(--muted-foreground)' }}>{formatDate(component.data.reservation_date)}</div>
                                  </div>
                                </div>
                              )}

                              {component.data.location && (
                                <div className="flex items-start gap-3 p-4 rounded-2xl" style={{ backgroundColor: 'var(--background)' }}>
                                  <MapPin className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: 'var(--accent)' }} />
                                  <div>
                                    <div className="text-sm font-semibold" style={{ color: 'var(--foreground)' }}>Location</div>
                                    <div style={{ color: 'var(--muted-foreground)' }}>{component.data.location}</div>
                                  </div>
                                </div>
                              )}
                            </>
                          )}

                          {/* Generic data fields for other components */}
                          {Object.entries(component.data).map(([key, value]) => {
                            if (key === 'property_name' || key === 'check_in_date' || key === 'check_out_date' ||
                                key === 'room_type' || key === 'reservation_time' || key === 'reservation_date' ||
                                key === 'location' || key === 'isOption' || key === 'optionDescription' || !value) {
                              return null;
                            }

                            const isPriceField = key.toLowerCase().includes('price') ||
                                                 key.toLowerCase().includes('cost') ||
                                                 key.toLowerCase().includes('fee') ||
                                                 key.toLowerCase().includes('total') ||
                                                 key.toLowerCase().includes('deposit');
                            const isNumeric = !isNaN(parseFloat(value)) && isFinite(value);

                            let displayValue = String(value);
                            if (isPriceField && isNumeric) {
                              displayValue = formatCurrency(parseFloat(value));
                            }

                            return (
                              <div key={key} className="flex items-start gap-3 p-4 rounded-2xl" style={{ backgroundColor: 'var(--background)' }}>
                                <Sparkles className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: 'var(--accent)' }} />
                                <div>
                                  <div className="text-sm font-semibold capitalize" style={{ color: 'var(--foreground)' }}>
                                    {humanizeKey(key)}
                                  </div>
                                  <div style={{ color: 'var(--muted-foreground)' }}>{displayValue}</div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Component Photos */}
                    {component.photos && component.photos.length > 0 && (
                      <div className="mt-6 px-4 sm:px-6 pb-4 sm:pb-6">
                        <h4 className="text-lg font-bold mb-3 flex items-center gap-2" style={{ color: 'var(--foreground)' }}>
                          <span>📸</span> Photos
                        </h4>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                          {component.photos.map((photo, photoIndex) => (
                            <div
                              key={photoIndex}
                              className="relative group cursor-pointer rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow"
                              style={{ backgroundColor: 'var(--background)' }}
                              onClick={() => window.open(photo.url, '_blank')}
                            >
                              <img
                                src={photo.url}
                                alt={photo.name || 'Component photo'}
                                className="w-full h-auto object-contain"
                                style={{ maxHeight: '300px' }}
                              />
                              <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-3">
                                <span className="text-white text-sm font-semibold">{photo.name || 'View Photo'}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Component Documents */}
                    {component.documents && component.documents.length > 0 && (
                      <div className="mt-6 px-4 sm:px-6 pb-4 sm:pb-6">
                        <h4 className="text-lg font-bold mb-3 flex items-center gap-2" style={{ color: 'var(--foreground)' }}>
                          <span>📋</span> Documents
                        </h4>
                        <div className="space-y-2">
                          {component.documents.map((doc, docIndex) => (
                            <a
                              key={docIndex}
                              href={doc.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center gap-3 px-4 py-3 rounded-xl transition-colors"
                              style={{ backgroundColor: 'var(--background)', border: '1px solid var(--border)' }}
                            >
                              <span className="text-2xl">📄</span>
                              <span className="font-medium" style={{ color: 'var(--foreground)' }}>{doc.name}</span>
                            </a>
                          ))}
                        </div>
                      </div>
                    )}

                    {hasMultipleOptions(component) && (
                      <div className="mt-6" style={{ paddingLeft: '1rem', paddingRight: '1rem', paddingBottom: '1rem' }}>
                        <h4 className="text-sm font-bold mb-3 uppercase tracking-wide" style={{ color: 'var(--foreground)' }}>Choose Your Option</h4>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          {getComponentOptions(component).map((option, optIndex) => (
                            <div key={option.id || optIndex} className="rounded-xl overflow-hidden" style={{ border: `2px solid ${optIndex === 0 ? 'var(--accent)' : 'var(--border)'}` }}>
                              {option.photoUrl && (
                                <img src={option.photoUrl} alt={option.label || 'Option'} className="w-full h-28 object-cover" />
                              )}
                              <div className="p-4">
                                <div className="flex items-center justify-between gap-2 mb-1">
                                  <span className="font-semibold text-sm" style={{ color: 'var(--foreground)' }}>{option.label || `Option ${optIndex + 1}`}</span>
                                  {option.badge && (
                                    <span className="text-xs px-2 py-0.5 rounded-full font-semibold" style={{ backgroundColor: 'var(--muted)', color: 'var(--accent)' }}>{option.badge}</span>
                                  )}
                                </div>
                                {option.description && (
                                  <p className="text-xs mb-2" style={{ color: 'var(--muted-foreground)' }}>{option.description}</p>
                                )}
                                <p className="text-lg font-bold" style={{ color: 'var(--accent)' }}>{formatCurrency(getOptionTotal(option))}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="backdrop-blur-sm rounded-3xl shadow-xl p-12 text-center" style={{ backgroundColor: 'var(--muted)', border: '1px solid var(--border)' }}>
              <Wand2 className="w-16 h-16 mx-auto mb-4" style={{ color: 'var(--muted-foreground)' }} />
              <p className="text-lg" style={{ color: 'var(--muted-foreground)' }}>Your magical itinerary is being prepared...</p>
            </div>
          )}
        </div>

        {/* Notes Section */}
        {quote.notes && (
          <div className="mt-8 backdrop-blur-sm rounded-3xl shadow-xl p-6 sm:p-8" style={{ backgroundColor: 'var(--muted)', border: '1px solid var(--border)' }}>
            <h3 className="text-2xl font-extrabold mb-4 flex items-center gap-2" style={{ color: 'var(--foreground)' }}>
              <Sparkles className="w-5 h-5" style={{ color: 'var(--accent)' }} />
              Special Notes
            </h3>
            <p className="whitespace-pre-wrap leading-relaxed" style={{ color: 'var(--muted-foreground)' }}>
              {quote.notes}
            </p>
          </div>
        )}

        {/* Pricing Footer - Grand Reveal */}
        <div className="mt-8 sm:mt-12 rounded-3xl shadow-2xl p-6 sm:p-12 text-center" style={{ backgroundColor: 'var(--primary)', color: 'var(--foreground)' }}>
          <div className="mb-6">
            <Sparkles className="w-12 h-12 mx-auto mb-4 animate-pulse" style={{ color: 'var(--accent)' }} />
            <h2 className="text-3xl sm:text-4xl font-extrabold mb-2">
              Your Magical Adventure Awaits!
            </h2>
            <p className="text-lg" style={{ color: 'var(--muted-foreground)' }}>
              Everything you need for an unforgettable journey
            </p>
          </div>

          <div className="backdrop-blur-sm rounded-2xl p-6 sm:p-8 mb-6 sm:mb-8" style={{ backgroundColor: 'rgba(255,255,255,0.08)' }}>
            <div className="text-sm font-semibold mb-2" style={{ color: 'var(--muted-foreground)' }}>Total Investment</div>
            <div className="text-5xl sm:text-6xl md:text-7xl font-black mb-2" style={{ color: 'var(--accent)' }}>
              {formatCurrency(quote.totalPrice)}
            </div>

            {quote.depositAmount > 0 && (
              <div className="border-t pt-4 mt-4" style={{ borderColor: 'rgba(255,255,255,0.2)' }}>
                <div className="flex items-center justify-center gap-2 mb-2" style={{ color: 'var(--muted-foreground)' }}>
                  <DollarSign className="w-5 h-5" />
                  <span className="font-semibold">Initial Deposit Due</span>
                </div>
                <div className="text-3xl font-extrabold">
                  {formatCurrency(quote.depositAmount)}
                </div>
                {quote.depositDueDate && (
                  <div className="text-sm mt-2" style={{ color: 'var(--muted-foreground)' }}>
                    Due by {formatDate(quote.depositDueDate)}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Payment Button */}
          {onPayment && (
            <button
              onClick={onPayment}
              disabled={isAgentPreview}
              className={`w-full font-extrabold py-4 sm:py-5 px-6 sm:px-8 rounded-2xl text-lg sm:text-xl transition-all duration-300 flex items-center justify-center gap-3 ${
                isAgentPreview ? 'opacity-50 cursor-not-allowed' : 'hover:scale-105 shadow-2xl'
              }`}
              style={{ backgroundColor: 'var(--accent)', color: 'var(--background)' }}
            >
              <CheckCircle2 className="w-6 h-6" />
              {isAgentPreview ? 'Preview Mode - Payment Disabled' : 'Approve Your Magic'}
              <Sparkles className="w-6 h-6" />
            </button>
          )}

          {!isAgentPreview && (
            <p className="text-sm mt-4" style={{ color: 'var(--muted-foreground)' }}>
              ✨ Let the magic begin! Your dream vacation is just one click away ✨
            </p>
          )}
        </div>

        {/* Agent Contact Footer */}
        {(quote.agent_name || quote.agent_email || quote.agent_phone) && (
          <div className="mt-12 rounded-3xl p-8" style={{ backgroundColor: 'var(--muted)', border: '2px solid var(--border)' }}>
            <div className="text-center mb-6">
              <Sparkles className="w-8 h-8 mx-auto mb-3" style={{ color: 'var(--accent)' }} />
              <h3 className="text-2xl font-bold mb-2" style={{ color: 'var(--foreground)' }}>Your Travel Advisor</h3>
              <div className="w-16 h-1 mx-auto rounded-full" style={{ backgroundColor: 'var(--accent)' }}></div>
            </div>

            <div className="max-w-md mx-auto">
              {quote.agent_name && (
                <div className="text-center mb-4">
                  <p className="text-xl font-bold" style={{ color: 'var(--foreground)' }}>{quote.agent_name}</p>
                  {quote.agent_business_name && (
                    <p className="font-semibold" style={{ color: 'var(--muted-foreground)' }}>{quote.agent_business_name}</p>
                  )}
                </div>
              )}

              <div className="space-y-3">
                {quote.agent_email && (
                  <a
                    href={`mailto:${quote.agent_email}`}
                    className="flex items-center justify-center gap-3 backdrop-blur-sm px-4 py-3 rounded-2xl transition-all shadow-sm hover:shadow-md"
                    style={{ backgroundColor: 'var(--background)' }}
                  >
                    <span className="text-2xl">📧</span>
                    <span className="font-medium" style={{ color: 'var(--foreground)' }}>{quote.agent_email}</span>
                  </a>
                )}

                {quote.agent_phone && (
                  <a
                    href={`tel:${quote.agent_phone}`}
                    className="flex items-center justify-center gap-3 backdrop-blur-sm px-4 py-3 rounded-2xl transition-all shadow-sm hover:shadow-md"
                    style={{ backgroundColor: 'var(--background)' }}
                  >
                    <span className="text-2xl">📞</span>
                    <span className="font-medium" style={{ color: 'var(--foreground)' }}>{quote.agent_phone}</span>
                  </a>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Footer Tagline */}
        <div className="mt-8 text-center">
          <p className="text-sm flex items-center justify-center gap-2" style={{ color: 'var(--muted-foreground)' }}>
            <Sparkles className="w-4 h-4" style={{ color: 'var(--accent)' }} />
            Where Dreams Come True
            <Sparkles className="w-4 h-4" style={{ color: 'var(--accent)' }} />
          </p>
        </div>
      </div>
    </div>
  );
};

export default MagicalTemplate;
